import React from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import useDesktop from '@components/shared/useDesktop';
import ArticleBody from '@components/vitals/ArticleBody';
import ArticleContributors from '@components/vitals/ArticleContributors';
import ArticleExtraContentItems from '@components/vitals/ArticleExtraContentItems';
import ArticleHeader from '@components/vitals/ArticleHeader';
import ArticleNextPrevButtons from '@components/vitals/ArticleNextPrevButtons';
import ArticleProductCards from '@components/vitals/ArticleProductCards';
import TableOfContents from '@components/vitals/components/TableOfContents';
import { createTitlesArray, filterHeadersFromTextContent } from '@components/vitals/utils/helpers';
import {
  ARTICLE_AUTHOR_WIDTH_DESKTOP,
  ARTICLE_AUTHOR_WIDTH_TABLET,
  ARTICLE_BODY_WIDTH_DESKTOP,
  ARTICLE_BODY_WIDTH_LAPTOP,
  BREAKPOINT
} from '@constants';
import { Article, ArticleSliceType, CategoryNode } from '@contracts';
import { hasAnyRelationsPrismic } from '@utils/helpers';
import ArticleWrittenBox from './components/ArticleWrittenBoxToBeDeleted';

export interface Props {
  url: string;
  currentArticleIndex: number;
  totalNumberOfArticles: number;
  activeAudio: string;
  articleContentRef: React.MutableRefObject<HTMLDivElement | null>;
  category: CategoryNode;
  article?: Article;
}

const ArticlePageContent = ({ currentArticleIndex, totalNumberOfArticles, activeAudio, articleContentRef, category, article }: Props) => {
  if (!article) return null;
  const isDesktop = useDesktop(BREAKPOINT.laptop);
  const { body, authors, illustrators, medical_reviewers } = article.data;

  const articleItemProductCards = body.find((item) => item.slice_type === ArticleSliceType.ProductCards);

  const articleItemsHeaders = filterHeadersFromTextContent(body);
  const articleTitleArray = createTitlesArray(articleItemsHeaders);

  // TODO delete all the following lines once https://app.asana.com/0/home/1207586784179529/1208456938900162 is unblocked
  const authorsRelationData = authors.map((item) => ({ ...item.article_author }));
  const medReviewersRelationData = medical_reviewers.map((item) => ({ ...item.medical_reviewer }));
  const illustratorsRelationData = illustrators.map((item) => ({ ...item.illustrator }));
  const isUsingNewContributorsInPrismic =
    hasAnyRelationsPrismic(authorsRelationData) ||
    hasAnyRelationsPrismic(medReviewersRelationData) ||
    hasAnyRelationsPrismic(illustratorsRelationData);

  return (
    <Container>
      <ArticleHeader article={article} category={category} />
      <ArticleContent>
        <TableOfContents titles={articleTitleArray} articleContentRef={articleContentRef} />
        <ArticleData ref={articleContentRef}>
          <AuthorsContainer $activeAudio={activeAudio}>
            {/* // TODO delete the conditional operator once https://app.asana.com/0/home/1207586784179529/1208456938900162 is unblocked */}
            {/* // leave only ArticleContributors */}
            {isUsingNewContributorsInPrismic ? (
              <ArticleContributors authors={authors} illustrators={illustrators} med_reviewers={medical_reviewers} />
            ) : (
              <ArticleWrittenBox article={article} />
            )}
          </AuthorsContainer>
          <BodyContainer>
            <ArticleBody article={article} activeAudio={activeAudio} headers={articleTitleArray} />
          </BodyContainer>
          {/* rendering ArticleProductCards on mobile in ArticleMainContentItem */}
          {/* because they need to be children of the innner Article body to be places between other children */}
          {isDesktop ? (
            <ContainerArticleProductCardsDesktop>
              <ArticleProductCards articleItem={articleItemProductCards} isMobile={!isDesktop} />
            </ContainerArticleProductCardsDesktop>
          ) : null}
        </ArticleData>
        {<ArticleExtraContentItems articleBody={article.data.body} />}
      </ArticleContent>
      {currentArticleIndex >= 0 && totalNumberOfArticles > 0 && (
        <ArticleNextPrevButtons currentArticleIndex={currentArticleIndex} totalNumberOfArticles={totalNumberOfArticles} />
      )}
    </Container>
  );
};

export default ArticlePageContent;

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  margin: 0;
  height: 100%;
  width: 100%;
`;

const ArticleContent = styled.div`
  width: 100%;
`;

const ArticleData = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  gap: 96px;
  padding: 96px 32px;
  background-color: ${Colors.cream};

  @media only screen and (max-width: ${BREAKPOINT.laptopM}px) {
    gap: 24px;
  }
  @media only screen and (max-width: ${BREAKPOINT.laptop}px) {
    flex-direction: column;
    align-items: center;
    gap: 32px;
    padding: 32px 32px;
  }
  @media only screen and (max-width: ${BREAKPOINT.tablet}px) {
    padding: 32px 16px;
  }
  @media only screen and (max-width: ${BREAKPOINT.mobileL}px) {
    .newsletter,
    .case-studies {
      width: 100vw;
      margin-left: -16px;
    }
  }
`;

const BodyContainer = styled.div`
  max-width: ${ARTICLE_BODY_WIDTH_DESKTOP}px;
  width: 100%;

  @media only screen and (max-width: ${BREAKPOINT.laptopL}px) {
    max-width: ${ARTICLE_BODY_WIDTH_LAPTOP}px;
  }
`;

interface AuthorsProps {
  $activeAudio: string;
}
const AuthorsContainer = styled.div<AuthorsProps>`
  max-width: ${ARTICLE_AUTHOR_WIDTH_DESKTOP}px;
  margin-bottom: ${({ $activeAudio }) => ($activeAudio ? 100 : 0)}px;

  @media only screen and (max-width: ${BREAKPOINT.laptop}px) {
    width: ${ARTICLE_AUTHOR_WIDTH_TABLET}px;
    max-width: 100%;
  }
`;

const ContainerArticleProductCardsDesktop = styled.div`
  position: sticky;
  top: 192px;
  margin-top: 96px;
`;
